import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { X } from 'lucide-react';
import { BRAND_NAME } from '../brand';
import {
  checkForUpdate,
  getUpdateState,
  subscribeUpdateState,
} from '../utils/updateCheck';

// "Update available" toast (operator 2026-08-07). A long-lived tab learns a new
// deploy is live (see utils/updateCheck.js for the two paths) and offers a
// one-click refresh instead of users needing a hard refresh — or white-screening
// on a dead chunk. Mounted once in App.js OUTSIDE the ErrorBoundary so it stays
// visible even while the routed tree is crashed on a chunk error.
//
// NEVER auto-reloads: dealership users have long forms mid-entry (deal desk,
// blog editor, settings drafts) — the choice to reload is theirs.

// Same cadence discipline as the Layout unread-count poll (perf P27): hidden
// tabs skip the tick, a focus return re-checks immediately. Kept as its own
// timer (not folded into Layout's) because Layout remounts on every route
// change and isn't mounted at all on login/public pages.
const POLL_INTERVAL_MS = 60000;

export function UpdateToast() {
  const { status, latestId } = useSyncExternalStore(subscribeUpdateState, getUpdateState);
  // In-memory per-tab: dismissing hides the toast for THIS deploy id only — it
  // re-appears on the next version bump (different id), never on a timer.
  const [dismissedId, setDismissedId] = useState(null);

  useEffect(() => {
    const tick = () => { if (!document.hidden) checkForUpdate(); };
    // No check on mount — a freshly loaded tab is (about to be) current.
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', tick);
    };
  }, []);

  const required = status === 'required';
  if (!required && (status !== 'available' || latestId === dismissedId)) return null;

  return (
    <div className="fixed bottom-4 inset-x-0 z-[70] flex justify-center px-4 pointer-events-none">
      <div
        data-testid={required ? 'update-toast-required' : 'update-toast'}
        role="status"
        className={`pointer-events-auto flex max-w-md items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${
          required
            ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100'
            : 'border-border bg-card text-foreground'
        }`}
      >
        <p className="min-w-0 flex-1 text-sm">
          {required
            ? `This tab is running an old version of ${BRAND_NAME} and needs a refresh.`
            : `A new version of ${BRAND_NAME} is available.`}
        </p>
        <button
          type="button"
          data-testid="update-toast-refresh"
          onClick={() => window.location.reload()}
          className="flex-shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          Refresh
        </button>
        {!required && (
          <button
            type="button"
            data-testid="update-toast-dismiss"
            aria-label="Dismiss"
            onClick={() => setDismissedId(latestId)}
            className="flex-shrink-0 rounded-md p-1 hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default UpdateToast;
